/**
 * Unread semantics for comments (spec D4).
 *
 * Pure predicates, deliberately outside the store: the panel, the tab badge and
 * every card must answer "is this new?" the same way, and components that mock
 * the store still need the real rule.
 */

import type { Comment } from '@/lib/comments-api'

/** The two per-target maps the reference is derived from. */
export interface UnreadReferenceState {
  watermarkByTargetId: Record<string, string | null>
  viewSnapshotByTargetId: Record<string, string | null>
}

/** Anything with an author and a birth time — comments and replies alike. */
export interface AuthoredItem {
  createdBy: string
  createdAt: string
}

/**
 * The cut-line "new" is measured against.
 *
 * While the Comments tab is open its snapshot wins — including a snapshot of
 * `null`, which is why this tests key *presence* rather than `snapshot ??
 * watermark`: a null-coalescing fallback would jump to the freshly advanced
 * watermark the moment the first-ever view landed, clearing the very pills the
 * user opened the tab to read.
 */
export function unreadReference(state: UnreadReferenceState, targetId: string): string | null {
  if (Object.prototype.hasOwnProperty.call(state.viewSnapshotByTargetId, targetId)) {
    return state.viewSnapshotByTargetId[targetId] ?? null
  }
  return state.watermarkByTargetId[targetId] ?? null
}

/** True while a comments surface holds an open view session for this target. */
export function hasViewSession(state: UnreadReferenceState, targetId: string): boolean {
  return Object.prototype.hasOwnProperty.call(state.viewSnapshotByTargetId, targetId)
}

/**
 * New = written by someone else after the reference. Your own writing is never
 * news to you, and a null reference (never viewed this artifact) makes
 * everything anyone else wrote unread.
 */
export function isUnread(
  item: AuthoredItem,
  reference: string | null,
  currentUserId: string | null,
): boolean {
  if (currentUserId !== null && item.createdBy === currentUserId) return false
  if (reference === null) return true
  // Parsed, not string-compared: the watermark comes back from a different
  // endpoint than the comment rows, and one serialising `+00:00` where the
  // other serialises `Z` would silently reorder identical instants.
  const at = Date.parse(item.createdAt)
  const cut = Date.parse(reference)
  if (Number.isNaN(at) || Number.isNaN(cut)) return false
  return at > cut
}

/** A thread is new if its comment or any of its replies is. */
export function isThreadUnread(
  comment: Comment,
  reference: string | null,
  currentUserId: string | null,
): boolean {
  if (isUnread(comment, reference, currentUserId)) return true
  return comment.replies.some((reply) => isUnread(reply, reference, currentUserId))
}

export function hasUnreadThreads(
  comments: readonly Comment[],
  reference: string | null,
  currentUserId: string | null,
): boolean {
  return comments.some((c) => isThreadUnread(c, reference, currentUserId))
}
